import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import bindClassNames from 'classnames/bind';

import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import Tabs from '@palette/components/designSystem/Tabs/Tabs';
import PlanV3List from '@palette/components/ic/planV3/PlanV3List/PlanV3List';
import Input from '@palette/components/designSystem/Input/Input';

import { useKeyInSearch } from '@palette/hooks/NavigationHooks';

import { PLANS_V3_PAGE_CONTENT_TABS_IDS, PLANS_V3_PAGE_CONTENT_QS_KEY } from '@palette/constants/tabs';

import { actions as PlanV3Actions, selectors as PlanV3Selectors } from '@palette/state/PlanV3';

import styles from './PlanV3PageContent.less';

const classNames = bindClassNames.bind(styles);

const PlanV3PageContent = ({ className }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [locationTab] = useKeyInSearch(PLANS_V3_PAGE_CONTENT_QS_KEY);

  const [searchedPlan, setSearchedPlan] = useState('');
  const [currentTab, setCurrentTab] = useState('');
  const [listLoaded, setListLoaded] = useState(false);

  const plansList = useSelector((state) => PlanV3Selectors.getList(state));

  const handleGetListSuccessCB = useCallback(() => {
    setListLoaded(true);
  }, [currentTab]);

  useEffect(() => {
    if (!currentTab && !locationTab) {
      setCurrentTab(PLANS_V3_PAGE_CONTENT_TABS_IDS.ACTIVE);
    } else if (locationTab && (locationTab !== currentTab)) {
      setCurrentTab(locationTab);
    }

    if ((currentTab === locationTab) || (!locationTab && currentTab === PLANS_V3_PAGE_CONTENT_TABS_IDS.ACTIVE)) {
      if (!listLoaded) {
        dispatch(PlanV3Actions.getICList({ onSuccessCB: handleGetListSuccessCB }));
      }
    }
  }, [currentTab, locationTab]);

  const getTabPropsByType = useCallback((type) => ({
    title: t(`masterPlansPageContent.tabs.${type}`),
    titleCount: plansList.length || 0,
    content: (
      <PlanV3List searchedPlan={searchedPlan} />
    ),
    defaultActive: type === PLANS_V3_PAGE_CONTENT_TABS_IDS.ACTIVE,
    tabKey: type,
  }), [plansList, searchedPlan]);

  const tabs = useMemo(() => [PLANS_V3_PAGE_CONTENT_TABS_IDS.ACTIVE].map((type) => getTabPropsByType(type)), [getTabPropsByType]);

  const handleTabChange = (key) => setCurrentTab(Object.values(PLANS_V3_PAGE_CONTENT_TABS_IDS)[key]);

  const actionsNodes = [];

  const toolboxNode = useMemo(() => (
    <div
      className={classNames({
        toolbox: true,
      })}
    >
      <Input
        className={styles.search}
        type="search"
        placeholder={t('masterPlansPageContent.planSearch.placeholder')}
        onChange={setSearchedPlan}
        value={searchedPlan}
      />
      <div className={styles.actionsWrapper}>
        {actionsNodes}
      </div>
    </div>
  ), [setSearchedPlan, searchedPlan, actionsNodes]);

  return (
    <div
      className={classNames({
        wrapper: true,
        [className]: className !== '',
      })}
    >
      <Tabs
        className={styles.tabs}
        tabs={tabs}
        onChange={handleTabChange}
        qsTabKey={PLANS_V3_PAGE_CONTENT_QS_KEY}
        tabBarExtraContent={toolboxNode}
      />
    </div>
  );
};

PlanV3PageContent.propTypes = {
  className: PropTypes.string,
};

PlanV3PageContent.defaultProps = {
  className: '',
};

export default PlanV3PageContent;
